// src/components/HeatMapCalendar.js
import React, { useEffect, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getDaysInMonth } from 'date-fns';
import { CircularProgress, Box, Typography } from '@mui/material';
import { auth, db } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';

const HeatMapCalendar = ({ userFamily, year, month }) => {
  const [heatMapData, setHeatMapData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHeatMapData = async (currentUser) => {
    if (!currentUser) return;
    
    try {
      const startDate = new Date(parseInt(year), parseInt(month) - 1, 1);
      const endDate = new Date(parseInt(year), parseInt(month), 0, 23, 59, 59);

      const expensesQuery = userFamily && userFamily.id
        ? query(
            collection(db, 'expenses'),
            where('familyId', '==', userFamily.id),
            where('date', '>=', startDate),
            where('date', '<=', endDate)
          )
        : query(
            collection(db, 'expenses'),
            where('userId', '==', currentUser.uid),
            where('date', '>=', startDate),
            where('date', '<=', endDate)
          );

      const snapshot = await getDocs(expensesQuery);
      const expensesData = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      }));

      const dayMap = {};
      expensesData.forEach((expense) => {
        const expenseDate = expense.date.toDate();
        const day = expenseDate.getDate();
        dayMap[day] = (dayMap[day] || 0) + (expense.amount || 0);
      });

      setHeatMapData(dayMap);
      setError(null);
    } catch (err) {
      console.error('Errore nel recupero dei dati:', err);
      setError('Errore nel caricamento dei dati.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchHeatMapData(user);
      } else {
        setError('Utente non autenticato');
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [year, month, userFamily]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body1" color="error" sx={{ textAlign: 'center' }}>
        {error}
      </Typography>
    );
  }

  const daysInMonth = getDaysInMonth(new Date(year, month - 1));
  const firstDayOfMonth = new Date(year, month - 1, 1).getDay();
  const maxAmount = Math.max(...Object.values(heatMapData), 1);

  const getIntensityClass = (amount) => {
    if (!amount) return 'color-empty';
    const intensity = amount / maxAmount;
    if (intensity >= 0.75) return 'color-scale-4';
    if (intensity >= 0.50) return 'color-scale-3';
    if (intensity >= 0.25) return 'color-scale-2';
    return 'color-scale-1';
  };

  const renderCalendarDays = () => {
    const days = [];
    // Giorni vuoti prima dell'inizio del mese
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    // Giorni del mese
    for (let day = 1; day <= daysInMonth; day++) {
      const amount = heatMapData[day] || 0;
      days.push(
        <div
          key={day}
          className={`calendar-day ${getIntensityClass(amount)}`}
          data-tooltip-id="calendar-tooltip"
          data-tooltip-content={`${day}: €${amount.toFixed(2)}`}
        >
          {day}
        </div>
      );
    }
    return days;
  };

  return (
    <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
      <div className="calendar-grid">
        {renderCalendarDays()}
      </div>
      <Tooltip id="calendar-tooltip" />
      <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption">Meno</Typography>
        {['color-empty', 'color-scale-1', 'color-scale-2', 'color-scale-3', 'color-scale-4'].map((colorClass) => (
          <Box
            key={colorClass}
            sx={{ width: 20, height: 20, borderRadius: 1, border: '1px solid #fff' }}
            className={colorClass}
          />
        ))}
        <Typography variant="caption">Più</Typography>
      </Box>
      <style>
        {`
          .calendar-grid {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            gap: 4px;
            padding: 10px;
          }
          .calendar-day {
            aspect-ratio: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 4px;
            font-size: 14px;
            color: #333;
            cursor: pointer;
          }
          .calendar-day.empty {
            background: transparent;
          }
          .color-empty {
            background-color: #ebedf0;
          }
          .color-scale-1 {
            background-color: #ffebee;
          }
          .color-scale-2 {
            background-color: #ffcdd2;
          }
          .color-scale-3 {
            background-color: #ef5350;
          }
          .color-scale-4 {
            background-color: #d32f2f;
            color: white;
          }
        `}
      </style>
    </Box>
  );
};

export default HeatMapCalendar;
